"use client";

import { motion } from 'framer-motion';
import { useState } from 'react';
import useSWR from 'swr';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRightIcon, CalendarDaysIcon, SparklesIcon } from '@heroicons/react/24/outline';

const API = '/api';
const fetcher = (url: string) => fetch(url).then(r => r.json());

const PLACEHOLDER_IMAGE = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 600'><rect width='600' height='600' fill='%23f8f3eb'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%23b08b62' font-family='Helvetica, Arial, sans-serif' font-size='24'>Image updating</text></svg>"
)}`;

function getKittenImage(kitten: any) {
  if (!kitten) return PLACEHOLDER_IMAGE;
  const photos = Array.isArray(kitten.photos) ? kitten.photos : [];
  return (
    kitten.imageUrl ||
    kitten.image_url ||
    kitten.main_photo ||
    kitten.mainPhoto ||
    photos[0] ||
    PLACEHOLDER_IMAGE
  );
}

export default function KittensPage() {
  const { data, error, isLoading } = useSWR(`${API}/kittens`, fetcher);
  const [selectedKitten, setSelectedKitten] = useState<any>(null);
  const list = Array.isArray(data) ? data : [];
  const availableCount = list.filter((kitten: any) => kitten.status?.toLowerCase() === 'available').length;
  const reservedCount = list.length > 0 ? list.filter((kitten: any) => kitten.status && kitten.status.toLowerCase() !== 'available').length : 0;
  const nextPlacementWindow = list.find((kitten: any) => kitten.readyDate)?.readyDate || 'Contact us';

  return (
    <main className="min-h-screen bg-gradient-to-br from-cream via-sand/20 to-soft-gold/30">
      {/* Intro */}
      <motion.section
        className="px-6 pt-20 pb-12"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="mx-auto max-w-6xl rounded-3xl border border-sand/25 bg-white/92 p-6 shadow-xl sm:p-8 lg:p-10">
          <div className="flex flex-col gap-8 md:flex-row md:items-stretch md:justify-between">
            <div className="space-y-6 md:max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-soft-gold/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-soft-gold">
                Atelier briefing
              </div>
              <div className="space-y-3">
                <h1 className="font-heading text-3xl font-bold text-warm-brown sm:text-4xl md:text-5xl">LINDY Balinese Kittens</h1>
                <p className="text-sm text-warm-brown/75 sm:text-base">
                  Salon-raised Balinese companions with daily serenity rituals and hands-on socialisation. Browse the nursery or begin a bespoke placement conversation in minutes.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                {[
                  {
                    label: 'Available now',
                    value: availableCount > 0 ? `${availableCount}` : 'Currently reserved',
                    icon: SparklesIcon,
                    tone: 'bg-sand/12 border-sand/30',
                  },
                  {
                    label: 'Reserved & maturing',
                    value: reservedCount > 0 ? `${reservedCount}` : 'Awaiting match',
                    icon: ArrowRightIcon,
                    tone: 'bg-sand/12 border-sand/30',
                  },
                  {
                    label: 'Next placement window',
                    value: nextPlacementWindow,
                    icon: CalendarDaysIcon,
                    tone: 'bg-soft-gold/15 border-soft-gold/30',
                  },
                ].map(({ label, value, icon: Icon, tone }) => (
                  <div key={label} className={`rounded-2xl border ${tone} p-4 shadow-sm`}
                  >
                    <div className="mb-2 flex items-center gap-2 text-soft-gold">
                      <Icon className="h-5 w-5" />
                      <span className="text-[0.7rem] uppercase tracking-[0.2em] font-semibold">{label}</span>
                    </div>
                    <p className="text-lg font-semibold text-warm-brown">{value}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Link
                  href="/kittens/application"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-soft-gold px-6 py-3 text-sm font-semibold text-warm-brown transition-transform hover:-translate-y-0.5 hover:bg-soft-gold/90"
                >
                  Begin application
                </Link>
                <Link
                  href="/waiting-list"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-soft-gold/40 px-6 py-3 text-sm font-semibold text-soft-gold transition-colors hover:bg-soft-gold/10"
                >
                  Join waiting list
                </Link>
                <Link
                  href="/process"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-sand/30 px-6 py-3 text-sm font-semibold text-warm-brown/70 transition-colors hover:bg-sand/15"
                >
                  Placement overview
                </Link>
              </div>
            </div>
            <div className="flex-1 rounded-3xl border border-sand/25 bg-cream/60 p-6 shadow-inner">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-soft-gold">Atelier ethos</p>
              <div className="mt-4 space-y-3 text-sm text-warm-brown/70">
                {[
                  'Calming soundscapes and aromatherapy anchor gentle temperaments.',
                  'Mindful handling elevates confidence and people-focused bonds.',
                  'Couture nutrition plans transition with you and your new companion.',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="mt-1 h-2 w-2 flex-none rounded-full bg-soft-gold" />
                    <p>{item}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-2xl border border-soft-gold/40 bg-white/85 p-4 text-xs text-warm-brown/65 sm:text-sm">
                Every placement includes veterinary dossiers, transition coaching, and lifetime breeder support.
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Kittens Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          {isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1,2,3,4,5,6].map(i => <SkeletonCard key={i} />)}
            </div>
          )}

          {error && (
            <motion.p
              className="text-center text-red-600 text-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              Failed to load kittens.
            </motion.p>
          )}

          {(!isLoading && list.length === 0) && (
            <motion.div
              className="text-center py-16"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <p className="text-xl text-warm-brown/60 mb-6">All current kittens are settling into their new LINDY families.</p>
              <p className="text-warm-brown/70 mb-8">Join the waiting list to receive bespoke litter previews, placement guidance, and early invitations to viewing salons.</p>
              <Link href="/waiting-list" className="px-8 py-3 bg-soft-gold text-warm-brown rounded-xl font-medium border border-soft-gold/80 hover:bg-soft-gold/85 hover:border-soft-gold transition-colors shadow-lg">
                Join the Waiting List
              </Link>
            </motion.div>
          )}

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {list.map((kitten: any, index: number) => (
              <KittenCard key={kitten.id} kitten={kitten} index={index} onSelect={setSelectedKitten} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-white/50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            className="bg-white/90 backdrop-blur rounded-3xl p-12 shadow-2xl"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-warm-brown mb-6">
              Ready for Your LINDY Companion?
            </h2>
            <p className="text-xl text-warm-brown/70 mb-8 max-w-2xl mx-auto">
              Every kitten placement begins with a personal consultation to ensure the perfect match between family and feline companion.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/waiting-list"
                className="bg-soft-gold text-warm-brown px-8 py-4 rounded-xl font-semibold hover:bg-soft-gold/80 transition-colors shadow-lg"
              >
                Join the Waiting List
              </Link>
              <Link
                href="/contact"
                className="border-2 border-soft-gold text-warm-brown px-8 py-4 rounded-xl font-semibold hover:bg-soft-gold/15 transition-all"
              >
                Schedule Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Modal */}
      {selectedKitten && (
        <KittenModal kitten={selectedKitten} onClose={() => setSelectedKitten(null)} />
      )}
    </main>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white/90 backdrop-blur rounded-3xl shadow-lg animate-pulse overflow-hidden">
      <div className="h-64 w-full bg-sand/20"></div>
      <div className="p-6">
        <div className="h-6 bg-sand/20 rounded w-1/2 mb-2"></div>
        <div className="h-4 bg-sand/20 rounded w-3/4 mb-3"></div>
        <div className="h-4 bg-sand/20 rounded w-1/3 mb-4"></div>
        <div className="h-10 bg-sand/20 rounded-xl w-full"></div>
      </div>
    </div>
  );
}

function KittenCard({ kitten, index, onSelect }: { kitten: any; index: number; onSelect: (kitten: any) => void }) {
  return (
    <motion.article
      className="group bg-white/90 backdrop-blur rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -8, scale: 1.02 }}
      onClick={() => onSelect(kitten)}
    >
      <div className="relative h-72 w-full overflow-hidden">
        <Image
          src={getKittenImage(kitten)}
          alt={kitten.name}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-110"
          sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-[0.1em] ${
            kitten.status === 'Available'
              ? 'bg-emerald-500/90 text-white'
              : 'bg-amber-500/90 text-white'
          }`}>
            {kitten.status}
          </span>
        </div>
        {kitten.price && (
          <div className="absolute top-4 right-4">
            <span className="px-3 py-1 rounded-full bg-soft-gold/90 text-white text-sm font-semibold">
              ${kitten.price.toLocaleString()}
            </span>
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="font-heading text-xl font-semibold text-warm-brown mb-2 group-hover:text-soft-gold transition-colors">
          {kitten.name}
        </h3>
        <div className="flex items-center justify-between mb-4">
          <div className="text-warm-brown/70 text-sm">
            <span>{kitten.age || 'Recently Born'}</span>
            <span className="mx-2">•</span>
            <span>{kitten.color}</span>
          </div>
        </div>
        <p className="text-warm-brown/70 text-sm leading-relaxed mb-4">
          {kitten.desc || `${kitten.name} has been nurtured with aromatherapy, sensory play, and abundant affection. Expect a confident, people-focused Balinese companion.`}
        </p>
        <button className="w-full py-3 bg-soft-gold/10 text-soft-gold rounded-xl border border-soft-gold/50 hover:bg-soft-gold hover:text-white hover:border-soft-gold transition-all duration-200 font-medium group-hover:shadow-lg">
          View Details
        </button>
      </div>
    </motion.article>
  );
}

function KittenModal({ kitten, onClose }: { kitten: any; onClose: () => void }) {
  return (
    <motion.div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative h-80 w-full">
          <Image
            src={getKittenImage(kitten)}
            alt={kitten.name}
            fill
            className="object-cover rounded-t-3xl"
            sizes="100vw"
          />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/80 backdrop-blur rounded-full flex items-center justify-center border border-white/70 hover:bg-white hover:border-white transition-colors shadow-sm"
          >
            <svg className="w-6 h-6 text-warm-brown" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-8">
          <h2 className="font-heading text-3xl font-bold text-warm-brown mb-4">{kitten.name}</h2>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-warm-brown/70">Age</p>
              <p className="font-semibold text-warm-brown">{kitten.age || 'Recently Born'}</p>
            </div>
            <div>
              <p className="text-warm-brown/70">Color</p>
              <p className="font-semibold text-warm-brown">{kitten.color}</p>
            </div>
            <div>
              <span className="text-warm-brown/70">Status</span>
              <p className="font-semibold text-warm-brown">{kitten.status === 'Available' ? 'Open for curated placement' : kitten.status}</p>
            </div>
            {typeof kitten.price === 'number' && (
              <div>
                <p className="text-warm-brown/70">Price</p>
                <p className="font-semibold text-soft-gold">${kitten.price.toLocaleString()}</p>
              </div>
            )}
            {typeof kitten.deposit_amount === 'number' && (
              <div>
                <p className="text-warm-brown/70">Deposit</p>
                <p className="font-semibold text-soft-gold">${kitten.deposit_amount.toLocaleString()}</p>
              </div>
            )}
            {/* Handle missing deposit but having price (fallback or calculated) */}
            {!kitten.deposit_amount && kitten.depositAmount && (
              <div>
                <p className="text-warm-brown/70">Deposit</p>
                <p className="font-semibold text-soft-gold">${Number(kitten.depositAmount).toLocaleString()}</p>
              </div>
            )}
          </div>
          <div className="prose prose-warm-brown max-w-none">
            <p className="text-warm-brown/80 leading-relaxed">
              {kitten.description || `${kitten.name} has been nurtured with aromatherapy, sensory play, and abundant affection. Expect a confident, people-focused Balinese companion ready to join a discerning household.`}
            </p>
          </div>
          <div className="mt-8 flex gap-4">
            <Link
              href={`/kittens/${kitten.id}`}
              className="flex-1 py-3 bg-soft-gold text-warm-brown rounded-xl font-semibold border border-soft-gold/80 hover:bg-soft-gold/85 hover:text-warm-brown hover:border-soft-gold transition-colors shadow-lg text-center"
            >
              View Full Details
            </Link>
            <Link
              href={`/kittens/application?kittenId=${kitten.id}&kittenName=${encodeURIComponent(kitten.name)}&kittenImage=${encodeURIComponent(getKittenImage(kitten))}&kittenAge=${encodeURIComponent(kitten.age)}&kittenColor=${encodeURIComponent(kitten.color)}&kittenGender=${encodeURIComponent(kitten.sex || 'Unknown')}&kittenPrice=${kitten.price || 0}&kittenPersonality=${encodeURIComponent(kitten.desc || kitten.personality || 'Playful and affectionate with a gentle nature')}`}
              className="flex-1 py-3 border border-soft-gold/80 text-soft-gold rounded-xl font-semibold hover:bg-soft-gold hover:text-white hover:border-soft-gold transition-colors shadow-md text-center"
            >
              Apply Now
            </Link>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
